import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  ListInput,
  LoadingSpinner,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  useDialogFormErrorNavigation,
  useToast,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import {
  FilterType,
  ReportShapeItem,
  ReportType,
  ShapeType,
} from '../../reportsTypes';
import {
  useCreateReportTemplateAndReport,
  useGetReportExportFormats,
} from '@ais-ui/websockets';
import { useCreateReportContext } from './CreateReportContext';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';
import { FilterFormField } from '../CloneReport/FilterFormField';
import { ShapeFormField } from '../CloneReport/ShapeFormField';
import { MiscellaneousFormField } from '../CloneReport/MiscellaneousFormField';
import { ShipFormField } from '../CloneReport/ShipFormField';
import { ShowTracksFormField } from '../components/ShowTracksFormField';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { getReportErrorMessage } from '../utils/reportErrorHandler';
import { useShapeAndFilterTypes } from './hooks/useShapeAndFilterTypes';
import { useQuickCreateInitialValues } from './hooks/useQuickCreateInitialValues';
import { useQuickCreateSchema } from './hooks/useQuickCreateSchema';
import { useReportFormContextBridge } from './hooks/useSyncQuickCreateContext';
import { useReportFormResources } from './hooks/useReportFormResources';
import { buildReportSubmission } from '../utils/reportSubmission';
import { useReportContext } from '../../ReportContext';
import { FormFieldTypes } from '../utils/formFieldTypes';
import { ProfileSelect } from '../components/ProfileSelect';
import { getMailActionOptions } from '../utils/mailActionMapping';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface QuickCreateWrapperProps {
  reportType: ReportType;
}

export const QuickCreateWrapper: React.FC<QuickCreateWrapperProps> = ({
  reportType,
}) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { toast } = useToast();

  // Fetch available export formats for this report type
  const { data: exportFormats } = useGetReportExportFormats(reportType.type);

  // Get mail action options based on available export formats
  const mailActionOptions = useMemo(
    () => getMailActionOptions(exportFormats),
    [exportFormats],
  );

  // Shared report resources
  const {
    shapes,
    filters,
    ports,
    databaseRange,
    isLoading: areResourcesLoading,
    isLoadingShareGroups,
  } = useReportFormResources({ reportType });

  const shapesRef = useRef(shapes);
  shapesRef.current = shapes;

  const { mutate } = useCreateReportTemplateAndReport();
  const {
    setReportTemplateName,
    setFirstReportName,
    setDateRange,
    setShowDialog,
    reportTemplateName,
    firstReportName,
    dateRange,
    email,
    setEmail,
    phone,
    setPhone,
    countryCode,
    setCountryCode,
    selectedGroups,
    setSelectedGroups,
    shapeDictionary,
    setShapeDictionary,
    filter,
    setFilter,
    ships,
    setShips,
    shipAttribute,
    setShipAttribute,
    miscellaneous,
    setMiscellaneous,
    shapeTab,
    resetSteps,
    activeTab,
    instantShape,
    instantShapeData,
    setIsDirty,
  } = useCreateReportContext();

  const { setShouldRefetch } = useReportContext();

  // Derived types + items
  const {
    filterType,
    setFilterType,
    shapeType,
    setShapeType,
    currentShapeItems,
  } = useShapeAndFilterTypes(reportType, {
    // Don't override shapeType with shapeTab - shapeTab is for UI tabs (polygon/circle/point)
    // while shapeType is for data structure (shape/multiShape/ports/worldShape)
    // shapeType: {
    //   value: shapeTab,
    //   onChange: (next) => setShapeTab(next),
    // },
    // Don't override filterType either - let it be determined by the reportType definition
    // The filterTab is only needed for UI tabs when there are multiple filter options
    // filterType: {
    //   value: filterTab,
    //   onChange: (next) => setFilterTab(next),
    // },
  });

  // Remove automatic filter type detection as it causes issues
  // The filter type should be determined by the report type definition, not the value
  const previousFilterRef = useRef(filter);

  useEffect(() => {
    previousFilterRef.current = filter;
  }, [filter]);

  const normalizedDateRange =
    dateRange?.from && dateRange?.to
      ? { from: dateRange.from, to: dateRange.to }
      : undefined;

  // Initial values from context
  const initialValues = useQuickCreateInitialValues({
    reportType,
    reportTemplateName,
    firstReportName,
    dateRange: normalizedDateRange,
    email,
    phone,
    countryCode,
    selectedGroups,
    miscellaneous,
    shapeDictionary: instantShape ? instantShapeData : shapeDictionary,
    filter,
    ships,
    shipAttribute,
    currentShapeItems: currentShapeItems as ReportShapeItem[] | undefined,
    shapeType: shapeType as ShapeType,
    filterType: filterType as FilterType,
    instantShape,
  });

  // Schema
  const schema = useQuickCreateSchema({
    currentShapeItems: currentShapeItems as ReportShapeItem[] | undefined,
    shapeType: shapeType as ShapeType,
    filterType: filterType as FilterType,
    reportType,
    databaseRange,
    messages: messages as Record<string, string>,
    formatDateTimeWithTimeZone: (value) =>
      formatDateTimeWithTimeZone(
        value instanceof Date || value === undefined ? value : new Date(value),
      ),
    instantShape,
    shapesRef,
  });

  const form: UseFormReturn<ReportCloneValues> = useForm<ReportCloneValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  // Sync form dirty state to context for exit warning
  useEffect(() => {
    setIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setIsDirty]);

  // Update form resolver when instantShape changes to prevent validation errors
  const previousInstantShapeRef = React.useRef(instantShape);
  React.useEffect(() => {
    if (previousInstantShapeRef.current !== instantShape) {
      // Clear ALL errors when switching modes - don't validate yet
      form.clearErrors();

      // Clear shape fields when toggling to prevent type mismatches
      if (currentShapeItems) {
        currentShapeItems.forEach((shapeItem) => {
          const currentValue = form.getValues(shapeItem.name as never);

          // If switching to instant shape mode and we have a string, clear it
          if (instantShape && typeof currentValue === 'string') {
            form.setValue(shapeItem.name as never, null as never, {
              shouldValidate: false,
            });
            form.clearErrors(shapeItem.name as never);
          }
          // If switching from instant shape mode and we have an object, clear it
          else if (
            !instantShape &&
            typeof currentValue === 'object' &&
            currentValue !== null
          ) {
            form.setValue(shapeItem.name as never, '' as never, {
              shouldValidate: false,
            });
            form.clearErrors(shapeItem.name as never);
          }
        });
      }

      previousInstantShapeRef.current = instantShape;
    }
  }, [instantShape, form, currentShapeItems]);

  // Loading state
  const isLoading = useMemo(
    () => areResourcesLoading || isLoadingShareGroups,
    [areResourcesLoading, isLoadingShareGroups],
  );

  // Form initialization / rehydration
  const [hasInitialized, setHasInitialized] = useState(false);
  const previousActiveTabRef = useRef<string | null>(null);

  // Always use current initial values when resetting the form
  // Don't cache them in a ref as it can become stale

  // Initialize form only once when component mounts or when switching TO this tab
  useEffect(() => {
    if (isLoading || !databaseRange) {
      return;
    }

    // Track if we just switched to this tab
    const justSwitchedToQuick =
      previousActiveTabRef.current !== null &&
      previousActiveTabRef.current !== 'quick' &&
      activeTab === 'quick';

    // Initial load: initialize once
    if (!hasInitialized) {
      form.reset(initialValues);
      setHasInitialized(true);
      previousActiveTabRef.current = activeTab;
      return;
    }

    // Tab switch: only reinitialize when switching FROM wizard TO quick
    // Use current initialValues, not a stale ref
    if (justSwitchedToQuick) {
      form.reset(initialValues);
      previousActiveTabRef.current = activeTab;
      return;
    }

    // Update the ref but don't reset the form
    previousActiveTabRef.current = activeTab;
  }, [
    hasInitialized,
    isLoading,
    databaseRange,
    form,
    activeTab,
    initialValues,
  ]);

  // Sync form -> context
  useReportFormContextBridge({
    form,
    reportType,
    shapes,
    filters,
    ports,
    shapeType: shapeType as ShapeType,
    filterType: filterType as FilterType,
    reportTemplateNameValue: reportTemplateName,
    firstReportNameValue: firstReportName,
    dateRangeValue: normalizedDateRange,
    miscellaneousValue: miscellaneous,
    shapeDictionaryValue: shapeDictionary,
    filterValue: filter,
    selectedGroupsValue: selectedGroups,
    instantShape,
    setReportTemplateName: (value) => setReportTemplateName(value ?? ''),
    setFirstReportName: (value) => setFirstReportName(value ?? ''),
    setDateRange: (range) => setDateRange(range),
    setEmail: (value) => setEmail(value ?? []),
    setPhone: (value) => setPhone(value ?? ''),
    setCountryCode: (value) => setCountryCode(value ?? ''),
    setSelectedGroups,
    setMiscellaneous: (value) => {
      setMiscellaneous(value);
    },
    setShapeDictionary,
    setFilter,
    setShips,
    setShipAttribute: setShipAttribute as React.Dispatch<
      React.SetStateAction<'colorBy' | 'filterBy' | undefined>
    >,
  });

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const onInvalid = useDialogFormErrorNavigation({
    containerRef: viewportRef,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (data: ReportCloneValues) => {
    setIsSubmitting(true);

    const submission = buildReportSubmission({
      reportType,
      values: data,
      shapeType: shapeType as ShapeType,
      filterType: filterType as FilterType,
      resources: {
        shapes,
        filters,
        ports,
      },
      instantShape,
    });

    if (!submission || !submission.params) {
      setIsSubmitting(false);
      toast({
        title: messages.genericToastTitleError,
        inViewport: viewportRef,
        description: 'Failed to build report parameters',
        variant: 'destructive',
      });
      return;
    }

    mutate(
      { type: reportType.type, params: submission.params },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          toast({
            className: 'create-report-success-toast',
            title: messages.genericToastTitleSuccess,
            description:
              messages.reportsCreateStartedSuccessMessage ||
              'Report creation started successfully',
          });
          setShowDialog(false);
          resetSteps();
          setShouldRefetch(true);
        },
        onError: (error: unknown) => {
          markErrorHandledLocally(error);
          const errorMessage = getReportErrorMessage(
            error as Error,
            messages.reportsCreateStartedFailedMessage ||
              'Failed to create report',
          );
          toast({
            className: 'create-report-error-toast',
            title: messages.genericToastTitleError,
            inViewport: viewportRef,
            description: errorMessage,
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      },
    );
  };
  const onSubmitWithValidation = form.handleSubmit(handleSubmit, onInvalid);

  if (isLoading) return <LoadingSpinner />;

  return (
    <div
      ref={viewportRef}
      className="flex flex-col h-full gap-4 transition-all"
    >
      <div className="flex-1 min-h-0">
        <Form {...form}>
          <form
            onSubmit={onSubmitWithValidation}
            className="h-full flex flex-col"
          >
            <ScrollArea className="h-[524px] w-full pr-4">
              <div className="space-y-6 px-px">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="report_template_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Report Template Name *</FormLabel>
                        <FormControl>
                          <Input
                            id="report-template-name"
                            type="text"
                            placeholder="Enter report template name"
                            {...field}
                            value={FormFieldTypes.asString(field)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="report_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Report Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="Enter report name"
                            {...field}
                            value={FormFieldTypes.asString(field)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="start_datetime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Start Date *</FormLabel>
                        <FormControl>
                          <DateTimePickerField
                            value={FormFieldTypes.asDate(field)}
                            onChange={(value) => field.onChange(value)}
                            dateLimit="past"
                            startDatetimeThreshold={
                              databaseRange?.database.begin
                            }
                            endDatetimeThreshold={databaseRange?.database.end}
                            className="w-72"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="end_datetime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>End Date *</FormLabel>
                        <FormControl>
                          <DateTimePickerField
                            value={FormFieldTypes.asDate(field)}
                            onChange={(value) => field.onChange(value)}
                            dateLimit="past"
                            startDatetimeThreshold={
                              databaseRange?.database.begin
                            }
                            endDatetimeThreshold={databaseRange?.database.end}
                            className="w-72"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {reportType.filter && (
                  <FilterFormField
                    form={form}
                    filterFields={reportType.filter}
                    filterType={filterType}
                    setFilterType={setFilterType}
                  />
                )}
                {reportType.ships && (
                  <ShipFormField form={form} shipsConfig={reportType.ships} />
                )}
                {reportType.ships &&
                  reportType.miscellaneous?.['showTracks'] && (
                    <ShowTracksFormField
                      form={form}
                      fieldConfig={reportType.miscellaneous?.['showTracks']}
                    />
                  )}
                {reportType.shape && (
                  <ShapeFormField
                    form={form}
                    shapeFields={reportType.shape}
                    shapeType={shapeType}
                    setShapeType={setShapeType}
                  />
                )}
                {reportType.prefilledFieldsMap && (
                  <ProfileSelect
                    prefilledFieldsMap={reportType.prefilledFieldsMap}
                    form={form}
                    prefix="miscellaneous."
                  />
                )}
                {reportType.miscellaneous && (
                  <MiscellaneousFormField
                    form={form}
                    miscellaneousFields={reportType.miscellaneous}
                    excludeFields={
                      reportType.ships ? ['showTracks'] : undefined
                    }
                    dateRange={dateRange}
                  />
                )}
              </div>
              <Separator className="my-6" />
              <div className="space-y-6">
                <FormField
                  key="email"
                  control={form.control}
                  name="miscellaneous.email"
                  render={({ field }) => (
                    <FormItem className="px-px">
                      <div className="flex justify-between">
                        <FormLabel>
                          {
                            messages.reportsCreateReportMiscellaneousNotifyEmailLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.reportsCreateReportMiscellaneousNotifyEmailTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl>
                        <ListInput
                          value={field.value || []}
                          onChange={field.onChange}
                          placeholder={
                            messages.reportsCreateReportMiscellaneousNotifyEmailPlaceholder ||
                            'Enter email and press Enter'
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  key="mailAction"
                  control={form.control}
                  name="miscellaneous.mailAction"
                  render={({ field }) => (
                    <FormItem className="px-px">
                      <div className="flex justify-between">
                        <FormLabel>
                          {messages.reportsCreateReportMiscellaneousMailActionLabel ||
                            'Email content'}
                        </FormLabel>
                        <InfoIconTooltip>
                          {messages.reportsCreateReportMiscellaneousMailActionTooltip ||
                            'Choose what to include in the email when the report completes'}
                        </InfoIconTooltip>
                      </div>
                      <Select
                        onValueChange={field.onChange}
                        value={FormFieldTypes.asString(field)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                messages.reportsCreateReportMiscellaneousMailActionPlaceholder ||
                                'Select email content'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent
                          key={`mail-options-${mailActionOptions.length}`}
                        >
                          {mailActionOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  key="selectedGroups"
                  control={form.control}
                  name="miscellaneous.selectedGroups"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between">
                        <FormLabel>
                          {
                            messages.reportsCreateReportMiscellaneousShareWithLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.reportsCreateReportMiscellaneousShareWithTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <GroupMultiSelect
                          {...field}
                          value={FormFieldTypes.asSelectArray(field)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </ScrollArea>
          </form>
        </Form>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Button
          variant="outline"
          onClick={() => {
            resetSteps();
          }}
          size="lg"
          disabled={isSubmitting}
        >
          Change Report Type
        </Button>
        <Button
          id="reports-creation-next-step-button"
          className="flex-1"
          type="button"
          onClick={onSubmitWithValidation}
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Report'}
        </Button>
      </div>
    </div>
  );
};
